import { Loader2 } from 'lucide-react';
import { Slot } from 'radix-ui';
import { forwardRef, memo, type ButtonHTMLAttributes } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { cn } from '../../lib/cn';

export const buttonVariants = tv({
  base: 'focus-visible:ring-ring inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium shadow-black/5 transition-all focus-visible:outline-none focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:scale-105 active:scale-100',
  variants: {
    variant: {
      primary:
        'bg-chat-primary text-chat-primary-foreground hover:bg-chat-primary/80 active:bg-chat-primary/50 shadow-sm',
      secondary:
        'bg-chat-secondary text-chat-secondary-foreground hover:bg-chat-secondary/80 active:bg-chat-secondary/50 shadow-sm',
      destructive:
        'bg-chat-destructive text-chat-destructive-foreground hover:bg-chat-destructive/80 active:bg-chat-destructive/50 shadow-sm',
      outline:
        'border-chat-input-border bg-chat-background hover:bg-chat-accent active:bg-chat-accent/50 border shadow-sm text-chat-foreground',
      ghost: '',
    },
    size: {
      xs: 'h-7 gap-1.5 px-3 text-xs [&_svg]:size-3.5',
      sm: 'h-8 gap-1.5 px-3.5 text-xs [&_svg]:size-4',
      md: 'h-9 px-4 py-2 [&_svg]:size-5',
      lg: '[&_svg]:size-6 h-10 rounded-lg px-4',
      xl: 'h-11 rounded-lg px-5 text-base [&_svg]:size-7',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
});

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof buttonVariants>, 'outline'> {
  asChild?: boolean;
  circle?: boolean;
  loading?: boolean;
}

export const Button = memo(
  forwardRef<HTMLButtonElement, ButtonProps>(
    (
      {
        className,
        children,
        type = 'button',
        variant,
        size,
        asChild = false,
        loading = false,
        circle = false,
        ...restProps
      },
      ref,
    ) => {
      const Comp = asChild ? Slot.Root : 'button';
      const content = loading ? (
        <>
          <Loader2 data-uui-button-loader className='animate-spin' />
          {children}
        </>
      ) : (
        children
      );

      return (
        <Comp
          type={asChild ? undefined : type}
          className={cn(
            buttonVariants({
              variant,
              size,
            }),
            circle && 'rounded-full',
            className,
          )}
          disabled={loading}
          ref={ref}
          {...restProps}
        >
          {content}
        </Comp>
      );
    },
  ),
);
